// services/auth.service.js
import TokenService from './token.service.js'
import { extractDetailMessage } from '../utils/errorDetail'

class AuthService {
  constructor() {
    this.baseURL = process.env.REACT_APP_AUTH_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
  }

  normalizeProfilePayload(profile = {}) {
    const avatar = profile.avatar_url || profile.avatarUrl || profile.avatar || '';
    return {
      ...profile,
      avatar_url: avatar,
      avatarUrl: avatar,
      avatar: avatar,
    };
  }

  /**
   * Realizar login de usuario
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise<Object>} Datos del usuario y tokens
   */
  async login(email, password) {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        email: email,
        password 
      })
    })

    const data = await response.json()
    
    // Verificar que la respuesta sea exitosa (HTTP status Y sin error en el body)
    if (!response.ok || data.status_code >= 400) {
      const detailMessage = data.detail && extractDetailMessage(data.detail);
      let errorMessage = data.message || detailMessage || `Login failed with status ${response.status}`;
      
      // Personalizar mensajes de error específicos
      if (data.status_code === 401 || data.message?.includes('Invalid credentials')) {
        errorMessage = 'Invalid email or password';
      } else if (data.message?.includes('not found')) {
        errorMessage = 'Account not found';
      } else if (data.message?.includes('verification') || data.message?.includes('verify')) {
        errorMessage = 'Please verify your email before logging in';
      } else if (data.error && Array.isArray(data.error) && data.error.length > 0) {
        // Si hay errores de validación específicos, usar el primero
        errorMessage = data.error[0] || errorMessage;
      }
      
      throw new Error(errorMessage)
    }

    // Verificar la estructura de la respuesta según lo que realmente devuelve la API
    let userData, tokens;
    
    if (data.body) {
      // Estructura real: { status_code: 200, message: '...', body: { jwt_token, username, etc } }
      const bodyData = data.body;
      
      // Calcular expires_in desde el JWT token (exp claim) o usar el valor de Cognito
      let expiresIn = bodyData.expires_in || 3600; // Default 1 hora si no viene
      
      // Si tenemos el JWT, extraer el exp real del token
      if (bodyData.jwt_token) {
        try {
          const jwtPayload = JSON.parse(atob(bodyData.jwt_token.split('.')[1]));
          if (jwtPayload.exp) {
            // Calcular seconds hasta expiración desde el exp del JWT
            const now = Math.floor(Date.now() / 1000);
            expiresIn = jwtPayload.exp - now;
            console.log('🔐 [Auth] Token expiration from JWT:', {
              exp: jwtPayload.exp,
              expiresIn: expiresIn,
              expiresInHours: (expiresIn / 3600).toFixed(2)
            });
          }
        } catch (e) {
          console.warn('⚠️ [Auth] Could not parse JWT for expiration, using default');
        }
      }
      
      tokens = {
        access_token: bodyData.jwt_token,
        jwt_token: bodyData.jwt_token,
        refresh_token: bodyData.refresh_token, // Guardar refresh token si viene
        id_token: bodyData.id_token, // Guardar id token si viene
        expires_in: expiresIn
      };
      
      // ✅ CORRECTO: Usar user_id y account_id que vienen del backend
      // El backend ahora retorna estos valores directamente de la DB
      userData = {
        user_id: bodyData.user_id,           
        account_id: bodyData.account_id,     
        default_account_id: bodyData.default_account_id, // ✅ NUEVO: Guardar default_account_id del login
        email: email,                        // ✅ Usar el email del login (no viene en body)
        username: bodyData.username,
        name: bodyData.name,
        last_name: bodyData.last_name,
        coins: bodyData.coins || 0
      };
      
      // Guardar default_account_id en localStorage para uso en AccountService
      if (bodyData.default_account_id) {
        localStorage.setItem('default_account_id', bodyData.default_account_id);
      }
      
    } else {
      // Fallback para otras estructuras (no debería pasar)
      userData = data.user || data;
      tokens = data.tokens || data;
    }

    // Guardar tokens automáticamente si existen
    if (tokens && tokens.access_token) {
      TokenService.saveTokens(tokens)
      
      // IMPORTANTE: Guardar también los datos del usuario para que persistan al recargar
      TokenService.saveUserData(userData)
    }
    
    const finalResult = { user: userData, tokens: tokens, rawData: data };
    
    // VALIDACIÓN FINAL: Asegurar que tenemos datos de usuario válidos
    if (!userData || !userData.user_id) {
      throw new Error('Authentication failed - no valid user data received')
    }
    
    return finalResult
  }

  /**
   * Registrar nuevo usuario
   * @param {Object} userData - Datos del usuario {email, password, username (=email), name, last_name}
   * @returns {Promise<Object>} Resultado del registro
   */
  async register(userData) {
    const response = await fetch(`${this.baseURL}/auth/sign-up`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
    })

    const data = await response.json()
    
    if (!response.ok || data.status_code >= 400) {
      const detailMessage = data.detail && extractDetailMessage(data.detail);
      throw new Error(data.message || detailMessage || 'Registration failed')
    }

    return data.body
  }

  /**
   * Verificar email con código de verificación
   * @param {string} email - Email del usuario
   * @param {string} username - Username para Cognito (debe ser igual al email)
   * @param {string} verificationCode - Código de verificación de 6 dígitos (se enviará como confirmation_code)
   * @returns {Promise<Object>} Resultado de la verificación
   */
  async verifyEmail(email, username, verificationCode) {
    const response = await fetch(`${this.baseURL}/auth/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        email,
        username,                           // ← Agregar username
        confirmation_code: verificationCode // ← Usar confirmation_code
      })
    })

    const data = await response.json()
    
    if (!response.ok || data.status_code >= 400) {
      const rawError = data.error
      const detailMessage = data.detail && extractDetailMessage(data.detail)
      const errorMessage = data.message ||
        (Array.isArray(rawError) ? rawError[0] : (typeof rawError === 'string' ? rawError : null)) ||
        detailMessage ||
        'Email verification failed'
      throw new Error(errorMessage)
    }

    return data.body
  }

  /**
   * Solicitar recuperación de contraseña
   * @param {string} email - Email del usuario
   * @returns {Promise<Object>} Confirmación de envío
   */
  async forgotPassword(email) {
    const response = await fetch(`${this.baseURL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email })
    })

    const data = await response.json()
    // Verificar que la respuesta sea exitosa (HTTP status Y sin error en el body)
    if (!response.ok || data.status_code >= 400) {
      const rawError = data.error
      const detailMessage = data.detail && extractDetailMessage(data.detail)
      const errorMessage = data.message ||
        (Array.isArray(rawError) ? rawError[0] : (typeof rawError === 'string' ? rawError : null)) ||
        detailMessage ||
        'Password reset request failed'
      throw new Error(errorMessage)
    }

    return data.body
  }

  /**
   * Resetear contraseña con código de confirmación
   * @param {string} email - Email del usuario
   * @param {string} confirmationCode - Código de confirmación
   * @param {string} newPassword - Nueva contraseña
   * @returns {Promise<Object>} Resultado del reset
   */
  async resetPassword(email, confirmationCode, newPassword) {
    const response = await fetch(`${this.baseURL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        email: email,  // Cambiar de vuelta a email
        code: confirmationCode,  // Cambiar confirmation_code por code
        new_password: newPassword 
      })
    })

    const data = await response.json()
    // Verificar que la respuesta sea exitosa (HTTP status Y sin error en el body)
    if (!response.ok || data.status_code >= 400) {
      const rawError = data.error
      const detailMessage = data.detail && extractDetailMessage(data.detail)
      const errorMessage = data.message ||
        (Array.isArray(rawError) ? rawError[0] : (typeof rawError === 'string' ? rawError : null)) ||
        detailMessage ||
        'Password reset failed'
      throw new Error(errorMessage)
    }

    return data.body
  }

  /**
   * Cambiar contraseña (requiere autenticación)
   * @param {string} currentPassword - Contraseña actual
   * @param {string} newPassword - Nueva contraseña
   * @returns {Promise<Object>} Resultado del cambio
   */
  async changePassword(currentPassword, newPassword) {
    const token = this.getAccessToken()
    
    if (!token) {
      throw new Error('No authentication token available')
    }
    
    const response = await fetch(`${this.baseURL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        jwt_token: token,
        old_password: currentPassword,
        new_password: newPassword
      })
    })

    const data = await response.json()
    
    if (!response.ok) {
      if (response.status === 401) {
        this.logout() // Token inválido
      }
      const detailMessage = data.detail && extractDetailMessage(data.detail)
      throw new Error(data.message || detailMessage || 'Password change failed')
    }

    return data.body
  }

  /**
   * Validar token actual con el servidor
   * @returns {Promise<Object>} Información del token y usuario
   */
  async validateToken() {
    const token = this.getAccessToken()
    
    if (!token) {
      throw new Error('No token available')
    }
    
    const response = await fetch(`${this.baseURL}/auth/validate-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        token: token
      })
    })

    const data = await response.json()
    
    if (!response.ok) {
      this.logout()
      const detailMessage = data.detail && extractDetailMessage(data.detail)
      throw new Error(data.message || detailMessage || 'Token validation failed')
    }

    return data.body
  }

  // =============================================================================
  // GESTIÓN DE TOKENS
  // =============================================================================

  /**
   * Guardar tokens en localStorage
   * @param {Object} tokens - Objeto con access_token, refresh_token, id_token, expires_in
   */
  saveTokens(tokens) {
    localStorage.setItem('access_token', tokens.access_token)
    
    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token)
    }
    
    if (tokens.id_token) {
      localStorage.setItem('id_token', tokens.id_token)
    }
    
    // Calcular tiempo de expiración
    const expiresAt = new Date(Date.now() + (tokens.expires_in * 1000))
    localStorage.setItem('token_expires_at', expiresAt.toISOString())
  }

  /**
   * Obtener access token
   * @returns {string|null} Access token o null si no existe
   */
  getAccessToken() {
    return TokenService.getAccessToken()
  }

  /**
   * Verificar si el token está expirado
   * @returns {boolean} true si el token está expirado
   */
  isTokenExpired() {
    return TokenService.isTokenExpired()
  }

  /**
   * Obtener refresh token
   * @returns {string|null} Refresh token o null si no existe
   */
  getRefreshToken() {
    return localStorage.getItem('refresh_token')
  }

  /**
   * Obtener ID token
   * @returns {string|null} ID token o null si no existe
   */
  getIdToken() {
    return localStorage.getItem('id_token')
  }

  // ...existing code...

  /**
   * Verificar si el usuario está autenticado
   * @returns {boolean} true si está autenticado y el token es válido
   */
  isAuthenticated() {
    const token = this.getAccessToken()
    return token && !this.isTokenExpired()
  }

  /**
   * Cerrar sesión y limpiar todos los datos
   */
  logout() {
    // Limpiar tokens
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('token_expires_at')
    
    // No es necesario limpiar user_data porque ya no lo guardamos
  }

  // =============================================================================
  // GESTIÓN DE DATOS DE USUARIO
  // =============================================================================

  /**
   * Obtener datos del usuario actual desde el JWT token o localStorage
   * @returns {Object|null} Datos del usuario
   */
  getCurrentUser() {
    // 1. Intentar obtener desde localStorage primero (datos completos)
    const savedUserData = TokenService.getUserData();
    if (savedUserData && savedUserData.user_id) {
      return savedUserData;
    }
    
    // 2. Si no hay en localStorage, decodificar del JWT
    const token = TokenService.getAccessToken();
    if (!token) return null;
    
    try {
      const jwtPayload = JSON.parse(atob(token.split('.')[1]));
      return {
        user_id: jwtPayload.sub || jwtPayload.user_id,
        account_id: jwtPayload.account_id || jwtPayload.sub,
        email: jwtPayload.email,
        username: jwtPayload.username || jwtPayload.preferred_username,
        name: jwtPayload.name || jwtPayload.given_name,
        last_name: jwtPayload.last_name || jwtPayload.family_name,
        coins: jwtPayload.coins || 0,
        ...jwtPayload
      };
    } catch (error) {
      console.error('❌ Error decodificando JWT:', error);
      return null;
    }
  }

  /**
   * Guardar datos del usuario en localStorage
   * @param {Object} userData - Datos del usuario
   */
  saveUserData(userData) {
    TokenService.saveUserData(userData)
  }

  /**
   * Verificar si el email del usuario está verificado
   * @returns {boolean} true si el email está verificado
   */
  isEmailVerified() {
    const user = this.getCurrentUser()
    return user?.email_verified || false
  }

  // =============================================================================
  // UTILIDADES
  // =============================================================================

  /**
   * Obtener headers para requests autenticados
   * @returns {Object} Headers con Authorization
   */
  getAuthHeaders() {
    const token = this.getAccessToken()
    
    if (!token || this.isTokenExpired()) {
      throw new Error('No valid token available')
    }
    
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  }

  /**
   * Obtener headers básicos para requests públicos
   * @returns {Object} Headers básicos
   */
  getPublicHeaders() {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  }

  /**
   * Verificar estado de autenticación y refrescar si es necesario
   * @returns {Promise<boolean>} true si está autenticado
   */
  async checkAuthStatus() {
    if (!this.isAuthenticated()) {
      return false
    }

    try {
      await this.validateToken()
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Obtener perfil de usuario desde el backend
   * @returns {Promise<Object>} Datos del usuario
   */
  async getUserProfile() {
    const token = this.getAccessToken();
    if (!token) throw new Error('No authentication token available');
    // Usar el endpoint correcto
    const response = await fetch(`${this.baseURL}/auth/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    const data = await response.json();
    if (!response.ok || data.status_code >= 400) {
      const detailMessage = data.detail && extractDetailMessage(data.detail);
      throw new Error(data.message || detailMessage || 'Failed to fetch user profile');
    }
    // El backend responde con { body: { ...userData } }
    return this.normalizeProfilePayload(data.body || {});
  }

  /**
   * Actualizar perfil de usuario
   * @param {Object} profileData - Datos del perfil a actualizar
   * @param {string} [profileData.username] - Nombre de usuario
   * @param {string} [profileData.name] - Nombre
   * @param {string} [profileData.last_name] - Apellido
   * @param {string} [profileData.phone_number] - Teléfono (formato: +15551234567)
   * @param {string} [profileData.timezone] - Zona horaria (formato: +05:00)
   * @param {string} [profileData.birth_date] - Fecha de nacimiento (debe ser pasado)
   * @param {string} [profileData.bio] - Biografía (máximo 500 caracteres)
   * @returns {Promise<Object>} Datos del perfil actualizado
   */
  async updateProfile(profileData) {
    const token = this.getAccessToken();
    if (!token) throw new Error('No authentication token available');

    // Validaciones del cliente
    const errors = {};

    // Phone number: +15551234567
    const phoneRegex = /^\+\d{1,15}$/;
    if (profileData.phone_number && !phoneRegex.test(profileData.phone_number)) {
      errors.phone_number = "Formato inválido. Debe ser: +15551234567";
    }

    // Timezone: +05:00 o -05:00
    const timezoneRegex = /^[+-]\d{2}:\d{2}$/;
    if (profileData.timezone && !timezoneRegex.test(profileData.timezone)) {
      errors.timezone = "Formato inválido. Debe ser: +05:00";
    }

    // Birth date (debe ser fecha pasada)
    if (profileData.birth_date && new Date(profileData.birth_date) >= new Date()) {
      errors.birth_date = "La fecha de nacimiento debe ser una fecha pasada";
    }

    // Bio max 500 caracteres
    if (profileData.bio && profileData.bio.length > 500) {
      errors.bio = "La biografía no puede exceder 500 caracteres";
    }

    // Si hay errores de validación, lanzar error
    if (Object.keys(errors).length > 0) {
      const errorMessage = Object.values(errors).join(', ');
      throw new Error(errorMessage);
    }

    // Usamos PUT (alias de PATCH en el backend: mismo update_user_profile_handler)
    // porque el preflight CORS del auth gateway no incluye PATCH en
    // Access-Control-Allow-Methods (solo OPTIONS,GET,POST,PUT,DELETE).
    const response = await fetch(`${this.baseURL}/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });

    const data = await response.json();
    
    if (!response.ok || data.status_code >= 400) {
      const detailMessage = data.detail && extractDetailMessage(data.detail);
      throw new Error(data.message || detailMessage || 'Failed to update profile');
    }

    // El backend responde con { body: { ...userData } }
    return this.normalizeProfilePayload(data.body || {});
  }

  /**
   * Subir avatar del usuario autenticado
   * POST /auth/avatar
   * @param {string} avatarData - Data URL base64 (ej: data:image/jpeg;base64,...)
   * @returns {Promise<Object>} Datos actualizados del perfil/avatar
   */
  async uploadAvatar(avatarData) {
    const token = this.getAccessToken();
    if (!token) throw new Error('No authentication token available');

    if (!avatarData || typeof avatarData !== 'string' || !avatarData.startsWith('data:image/')) {
      throw new Error('Invalid avatar data format');
    }

    const response = await fetch(`${this.baseURL}/auth/avatar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ avatar_data: avatarData })
    });

    const data = await response.json();

    if (!response.ok || data.status_code >= 400) {
      const detailMessage = data.detail && extractDetailMessage(data.detail);
      throw new Error(data.message || detailMessage || 'Failed to upload avatar');
    }

    return this.normalizeProfilePayload(data.body || {});
  }

  /**
   * Refresh API Key
   * @param {string} accountId - ID de la cuenta
   * @returns {Promise<Object>} Nueva API key
   */
  async refreshApiKey(accountId) {
    const token = this.getAccessToken();
    if (!token) throw new Error('No authentication token available');

    const response = await fetch(`${this.baseURL}/auth/api-key/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ account_id: accountId })
    });

    const data = await response.json();
    
    if (!response.ok || data.status_code >= 400) {
      const detailMessage = data.detail && extractDetailMessage(data.detail);
      throw new Error(data.message || detailMessage || 'Failed to refresh API key');
    }

    return {
      success: true,
      data: data.body || {}
    };
  }
}

// Exportar instancia singleton
const instance = new AuthService();
export default instance;
